import { Box, Typography } from '@mui/material';
import { isNil, lowerCase, map, uniq, upperFirst } from 'lodash';
import { useMemo } from 'react';

import currentsNodeIcon from '@/assets/currents-node.png';
import { LineArea } from '@/charts';
import { convert } from '@/common/convert';
import { ColorIcon, LineIcon } from '@/components/data/ColorIcon';
import { Datum } from '@/components/data/Datum';
import { DirectionDatum } from '@/components/data/DirectionDatum';
import { CurrentsChartDatum } from '@/components/spotter/historicalDataTab/CurrentsLineChartSection';
import {
    DataDetailDatum,
    DataDetailsSection,
} from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import { MissingDataInfo } from '@/components/spotter/historicalDataTab/datumDetail/MissingDataInfo';

export type CurrentsDatumDetailProps = {
    datum: CurrentsChartDatum;
    dataset: CurrentsChartDatum[];
    line: LineArea<CurrentsChartDatum>;
};

export const CurrentsDatumDetail = ({ datum, line, dataset }: CurrentsDatumDetailProps) => {
    const sensorLabel = useMemo(
        () =>
            datum.sensorPosition && uniq(map(dataset, 'sensorPosition')).length > 1
                ? upperFirst(lowerCase(String(datum.sensorPosition)))
                : null,
        [dataset, datum.sensorPosition]
    );
    const tiltDeg = convert(datum.sensorTilt).from(datum.sensorTiltUnits).to('deg');
    const overTilt = isNil(datum.speed) && !isNil(datum.sensorTilt);

    return (
        <DataDetailsSection label={sensorLabel}>
            {overTilt ? (
                <MissingDataInfo>
                    Current data was not collected during this time because the current meter tilt exceeded the sensor
                    threshold.
                </MissingDataInfo>
            ) : (
                <>
                    <DataDetailDatum label="Mean current speed" icon={<LineIcon dot color={line.color.line} />}>
                        <Datum value={datum.speed} type="currentSpeed" sourceUnit={datum.speedUnits} />
                    </DataDetailDatum>
                    <DataDetailDatum label="Std. deviation" icon={<ColorIcon color={line.color.area} />}>
                        {isNil(datum.speedStdDev) ? (
                            <Typography component="em" sx={{ whiteSpace: 'nowrap' }}>
                                Unknown
                            </Typography>
                        ) : (
                            <Datum value={datum.speedStdDev} type="currentSpeed" sourceUnit={datum.speedUnits} />
                        )}
                    </DataDetailDatum>
                    <DataDetailDatum label="Current direction">
                        <DirectionDatum
                            iconSx={{ fontSize: '1em' }}
                            heading={datum.direction}
                            type="flow"
                            unit={datum.directionUnits}
                        />
                    </DataDetailDatum>
                </>
            )}
            <DataDetailDatum label="Sensor tilt">
                <Box sx={{ position: 'relative', height: '24px', width: '24px' }}>
                    <Box sx={{ position: 'absolute', height: '2px', width: 1, top: '50%', bgcolor: 'darkgray' }} />
                    <Box sx={{ position: 'absolute', height: 1, width: 1, transform: `rotate(${tiltDeg}deg)` }}>
                        <Box sx={{ position: 'absolute', height: '1px', width: 1, top: '50%', bgcolor: 'blue' }} />
                        <Box component="img" src={currentsNodeIcon} sx={{ height: '100%' }} />
                    </Box>
                </Box>
                <Datum value={datum.sensorTilt} type="angle" sourceUnit={datum.sensorTiltUnits} />
            </DataDetailDatum>
        </DataDetailsSection>
    );
};
